import { io, Socket } from "socket.io-client";
import { store } from "src/redux/store";

type SocketAckSuccess<T = any> = {
  ok: true;
  data: T;
};

type SocketAckError = {
  ok: false;
  error?: string;
};

type SocketAckResponse<T = any> = SocketAckSuccess<T> | SocketAckError;

const DEFAULT_ACK_TIMEOUT_MS = 10000;

let socket: Socket | null = null;
let lastToken: string | null = null;
let unsubscribeStore: (() => void) | null = null;
let lifecycleConfigured = false;

export type SocketConnectionStatus =
  | "idle"
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

export type SocketConnectionSnapshot = {
  status: SocketConnectionStatus;
  error: string | null;
};

let connectionSnapshot: SocketConnectionSnapshot = {
  status: "idle",
  error: null,
};

const connectionSubscribers = new Set<
  (snapshot: SocketConnectionSnapshot) => void
>();

const updateConnectionSnapshot = (
  status: SocketConnectionStatus,
  error: string | null = null
) => {
  connectionSnapshot = { status, error };
  connectionSubscribers.forEach((subscriber) => subscriber(connectionSnapshot));
};

const getSocketUrl = () =>
  process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || "";

const getAuthToken = () => store.getState().auth?.accessToken || "";

const configureSocketLifecycle = (client: Socket) => {
  if (lifecycleConfigured) return;
  lifecycleConfigured = true;

  client.on("connect", () => {
    updateConnectionSnapshot("connected");
    if (process.env.NODE_ENV !== "production") {
      console.info("[socket] conectado", { socketId: client.id });
    }
  });

  client.on("connect_error", (error) => {
    const message = error?.message || "Falha ao conectar ao Socket.IO";
    updateConnectionSnapshot("error", message);
    console.error("[socket] erro de conexão:", message);
  });

  client.on("disconnect", (reason) => {
    updateConnectionSnapshot("disconnected");
    if (process.env.NODE_ENV !== "production") {
      console.info("[socket] desconectado", { reason });
    }
  });

  client.io.on("reconnect_attempt", () => {
    updateConnectionSnapshot("connecting");
  });

  client.io.on("reconnect_failed", () => {
    updateConnectionSnapshot("error", "Falha ao reconectar ao Socket.IO");
  });
};

const syncSocketAuthFromStore = () => {
  const token = getAuthToken();
  if (token === lastToken) return;

  lastToken = token;
  if (!socket) return;

  socket.auth = { token };
  socket.disconnect();

  if (token) {
    updateConnectionSnapshot("connecting");
    socket.connect();
  } else {
    updateConnectionSnapshot("disconnected");
  }
};

const ensureStoreSubscription = () => {
  if (!unsubscribeStore) {
    unsubscribeStore = store.subscribe(syncSocketAuthFromStore);
  }
};

const ensureSocketAuth = (client: Socket) => {
  const token = getAuthToken();
  if (token === lastToken) return;

  lastToken = token;
  client.auth = { token };

  // Reconnect to refresh handshake auth when token changes.
  if (client.connected) {
    client.disconnect();
  }

  if (token && !client.connected && !client.active) {
    updateConnectionSnapshot("connecting");
    client.connect();
  }
};

export const getSocketClient = () => {
  ensureStoreSubscription();
  const socketUrl = getSocketUrl();

  if (!socketUrl) {
    throw new Error(
      "Socket URL não configurada. Defina REACT_APP_SOCKET_URL ou REACT_APP_API_URL."
    );
  }

  if (!socket) {
    const token = getAuthToken();
    lastToken = token;

    socket = io(socketUrl, {
      autoConnect: false,
      transports: ["websocket", "polling"],
      auth: { token },
    });
    configureSocketLifecycle(socket);
  }

  ensureSocketAuth(socket);

  if (getAuthToken() && !socket.connected && !socket.active) {
    updateConnectionSnapshot("connecting");
    socket.connect();
  }

  return socket;
};

export const disconnectSocketClient = () => {
  socket?.disconnect();
};

export const getSocketConnectionSnapshot = () => connectionSnapshot;

export const subscribeSocketConnection = (
  subscriber: (snapshot: SocketConnectionSnapshot) => void
) => {
  connectionSubscribers.add(subscriber);
  subscriber(connectionSnapshot);

  return () => {
    connectionSubscribers.delete(subscriber);
  };
};

const waitForSocketConnection = (
  client: Socket,
  timeoutMs: number
): Promise<void> => {
  if (client.connected) return Promise.resolve();
  if (!getAuthToken()) {
    return Promise.reject(new Error("Socket sem token de autenticação"));
  }

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timer);
      client.off("connect", handleConnect);
      client.off("connect_error", handleConnectError);
    };

    const handleConnect = () => {
      cleanup();
      resolve();
    };

    const handleConnectError = (error: Error) => {
      cleanup();
      reject(error);
    };

    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error("Timeout ao conectar ao Socket.IO"));
    }, timeoutMs);

    client.on("connect", handleConnect);
    client.on("connect_error", handleConnectError);

    if (client.connected) {
      handleConnect();
      return;
    }

    if (!client.active) {
      updateConnectionSnapshot("connecting");
      client.connect();
    }
  });
};

export const emitSocketAck = async <T = any>(
  event: string,
  payload?: any,
  timeoutMs: number = DEFAULT_ACK_TIMEOUT_MS
): Promise<T> => {
  const client = getSocketClient();
  await waitForSocketConnection(client, timeoutMs);

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      callback();
    };

    const timer = window.setTimeout(() => {
      finish(() => {
        reject(new Error(`Socket ack timeout (${event})`));
      });
    }, timeoutMs);

    try {
      client.emit(event, payload ?? {}, (ack: SocketAckResponse<T>) => {
        finish(() => {
          if (!ack || typeof ack !== "object") {
            reject(new Error(`Socket ack inválido (${event})`));
            return;
          }

          if ((ack as SocketAckError).ok === false) {
            reject(
              new Error(
                (ack as SocketAckError).error ||
                  `Socket request falhou (${event})`
              )
            );
            return;
          }

          resolve((ack as SocketAckSuccess<T>).data);
        });
      });
    } catch (error) {
      finish(() => {
        reject(
          error instanceof Error
            ? error
            : new Error(`Erro ao emitir socket event (${event})`)
        );
      });
    }
  });
};
